import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetRelationTypesDocument,
  UpsertRelationTypeDocument,
  type UpsertRelationTypeMutation,
  type UpsertRelationTypeMutationVariables,
} from "~/features/admin/relationTypes/gqls";
import type { RelationTypeFormData } from "~/features/admin/relationTypes/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseRelationTypeCreateOptions {
  onSuccess?: () => void;
}

/**
 * Create a relation type via `cnutUpsertRelationType` (V1 parity). Refetches the
 * tenant-scoped list so the new row appears.
 */
export function useRelationTypeCreate({ onSuccess }: UseRelationTypeCreateOptions = {}) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [upsertRelationType] = useMutation<UpsertRelationTypeMutation, UpsertRelationTypeMutationVariables>(
    UpsertRelationTypeDocument,
    { refetchQueries: [{ query: GetRelationTypesDocument, variables: { tenantId } }] },
  );

  const submit = useCallback(
    async (formData: RelationTypeFormData) => {
      setProcessing(true);
      try {
        await upsertRelationType({
          variables: {
            input: {
              tenantId,
              name: formData.name,
              sourceCategoryId: formData.sourceCategoryId,
              targetCategoryId: formData.targetCategoryId,
            },
          },
        });
        toast.success(t("feature.common.createdSuccessfully", { model: "relationType" }));
        onSuccess?.();
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [upsertRelationType, tenantId, t, onSuccess],
  );

  return { submit, processing };
}
